import { valueAliasRole } from '@orientaction/utils';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {
  BENEFICIARY_ROLE_ID,
  CONSULTANT_ROLE_ID,
  DIRECTION_ROLE_ID,
  MANAGER_2_ROLE_ID,
  MANAGER_3_ROLE_ID,
} from './constants';
import { CategoryTestOption, ConsultantSummary, ManagerOption, RoleOption, ServiceOption } from './types';

export const toDayOfBirth = (value?: string) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
};

export const unwrapCollection = (payload: any): any[] => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  return [];
};

export const unwrapRelation = (value: any): any[] => {
  if (value == null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (Array.isArray(value?.data)) {
    return value.data;
  }
  return [];
};

export const toId = (value: any): number | null => {
  const id = Number(typeof value === 'object' ? value?.id : value);
  return Number.isFinite(id) && id > 0 ? id : null;
};

export const toIdList = (values?: any[]): number[] =>
  (values || []).map(toId).filter((id): id is number => id !== null);

export const categoryTestLabel = (category: Pick<CategoryTestOption, 'name' | 'subtitle'>) => {
  const name = (category.name || '').trim();
  const subtitle = (category.subtitle || '').trim();
  if (name && subtitle) {
    return `${name} : ${subtitle}`;
  }
  return name || subtitle;
};

export const mapCategoryTest = (item: any): CategoryTestOption | null => {
  const id = toId(item);
  if (!id) {
    return null;
  }
  const attrs = item?.attributes || item;
  return {
    id,
    name: attrs.name || '',
    subtitle: attrs.subtitle || '',
    priority: Number(attrs.priority ?? 0),
  };
};

export const mapCategoryTests = (raw: any): CategoryTestOption[] =>
  unwrapCollection(raw)
    .map(mapCategoryTest)
    .filter((item): item is CategoryTestOption => item !== null)
    .sort((a, b) => a.priority - b.priority || a.name.localeCompare(b.name, 'fr'));

export const toServiceOption = (service: any): ServiceOption | null => {
  if (service == null) {
    return null;
  }
  const id = Number(typeof service === 'object' ? service.id : service);
  if (!id) {
    return null;
  }
  return {
    id,
    name: service?.name || service?.attributes?.name || `Service ${id}`,
  };
};

export const normalizeServiceList = (raw: any): ServiceOption[] => {
  const list = unwrapRelation(raw);
  if (list.length) {
    return list.map(toServiceOption).filter((item): item is ServiceOption => Boolean(item));
  }
  if (raw && typeof raw === 'object' && (raw.id || raw.attributes)) {
    const option = toServiceOption(raw);
    return option ? [option] : [];
  }
  return [];
};

export const toResourceFromTemplate = (
  item: any,
  group?: string,
  groupSubtitle?: string,
  groupId?: number | null
) => {
  const id = Number(item.id);
  const attrs = item.attributes || item;
  const label = attrs.name || attrs.title || item.name || item.title || `Ressource ${id}`;
  return {
    id,
    label,
    group,
    groupSubtitle: groupSubtitle || undefined,
    groupId: groupId && groupId > 0 ? groupId : undefined,
    query: attrs.query || item.query,
  };
};

export const resourceMatchesService = (item: any, selectedServiceId: number) => {
  const services = item?.authorizations?.servicesGame;
  if (!Array.isArray(services) || services.length === 0) {
    return false;
  }
  return services.some((service: any) => Number(service.id) === Number(selectedServiceId));
};

export const readUpperHierarchy = (raw: any) => {
  if (!raw) {
    return null;
  }
  return raw.data ? { id: raw.data.id, ...(raw.data.attributes || {}) } : raw;
};

export const readRole = (value: any): { id: number; name?: string } | null => {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number') {
    return { id: value };
  }
  const node = value.data && value.id == null ? value.data : value;
  const id = Number(node?.id ?? node?.attributes?.id);
  if (!id) {
    return null;
  }
  return {
    id,
    name: node.name || node.attributes?.name || node.data?.attributes?.name,
  };
};

const DEFAULT_STAFF_ROLE_NAMES: Record<number, string> = {
  [DIRECTION_ROLE_ID]: 'Direction',
  [MANAGER_3_ROLE_ID]: 'manager_3',
  [MANAGER_2_ROLE_ID]: 'manager_2',
  [CONSULTANT_ROLE_ID]: 'manager_1',
};

export const getStaffRoleLabel = (
  role?: { id?: number; name?: string } | null,
  companyUserRoleAlias?: any
) => {
  const id = Number(role?.id);
  const rawName = role?.name || DEFAULT_STAFF_ROLE_NAMES[id] || '';
  return valueAliasRole(rawName, companyUserRoleAlias) || rawName || 'Consultant';
};

const staffRoleSortRank = (roleId?: number) => {
  const id = Number(roleId);
  if (id === CONSULTANT_ROLE_ID) {
    return 0;
  }
  if (id === DIRECTION_ROLE_ID) {
    return 1;
  }
  if (id === MANAGER_3_ROLE_ID) {
    return 2;
  }
  if (id === MANAGER_2_ROLE_ID) {
    return 3;
  }
  return 4;
};

export const sortAssignableStaff = <T extends ConsultantSummary>(list: T[]): T[] =>
  [...list].sort((a, b) => {
    const rank =
      staffRoleSortRank(a.company_user_role?.id) - staffRoleSortRank(b.company_user_role?.id);
    if (rank !== 0) {
      return rank;
    }
    return `${a.lastname || ''} ${a.firstname || ''}`.localeCompare(
      `${b.lastname || ''} ${b.firstname || ''}`,
      'fr'
    );
  });

export const staffRoleGroupBy = (option: ConsultantSummary) => option.roleLabel || 'Consultant';

export const hasMultipleStaffRoleGroups = (list: ConsultantSummary[]) =>
  new Set(list.map(item => item.roleLabel).filter((label): label is string => Boolean(label)))
    .size > 1;

export const restrictsMinorTestsToCategories = (roleId?: number) =>
  Number(roleId) === CONSULTANT_ROLE_ID;

export const filterMinorCategoriesForStaff = (
  categories: any[],
  staff?: {
    company_user_role?: { id?: number } | null;
    categoryIds?: Array<number | string>;
  } | null,
  options?: { showAllWhenConsultantHasNone?: boolean }
) => {
  if (!restrictsMinorTestsToCategories(staff?.company_user_role?.id)) {
    return categories;
  }
  const allowed = new Set((staff?.categoryIds || []).map(Number).filter(id => id > 0));
  if (!allowed.size) {
    return options?.showAllWhenConsultantHasNone ? categories : [];
  }
  return categories.filter(category => allowed.has(Number(category.id)));
};

export const needsAttachmentManager = (roleId: number | '') =>
  roleId !== '' && Number(roleId) !== DIRECTION_ROLE_ID;

export const mapRoleOptions = (
  companyUserRole: any,
  options: {
    isSuperAdmin: boolean;
    connectedRoleId: number;
    aliasName?: (name: string) => string;
    excludeRoleIds?: number[];
  }
): RoleOption[] => {
  const exclude = new Set(options.excludeRoleIds ?? [BENEFICIARY_ROLE_ID]);
  const list = unwrapCollection(companyUserRole)
    .map((item: any) => {
      const id = Number(item.id);
      const name = item.attributes?.name || item.name || '';
      return {
        id,
        name: options.aliasName ? options.aliasName(name) : name,
      };
    })
    .filter((role: RoleOption) => role.id && !exclude.has(role.id));
  const start = options.isSuperAdmin ? 0 : options.connectedRoleId;
  return list.filter((role: RoleOption) => role.id >= start);
};

export const buildUpHierarchyPayload = ({
  companyId,
  roleId,
  isSuperAdmin,
  userConnected,
}: {
  companyId: number | string;
  roleId: number | '';
  isSuperAdmin: boolean;
  userConnected: any;
}) => {
  const payload: any = {
    company: companyId,
    filter: '',
    company_user_role: Number(roleId),
    equal: false,
  };
  if (isSuperAdmin) {
    payload.direction = userConnected.Direction || null;
  } else {
    payload.zoneInfluance = {
      name: userConnected.company_user_role?.id,
      value: userConnected.id,
    };
  }
  return payload;
};

export const mapManagerOptions = (payload: any, excludeId?: number): ManagerOption[] =>
  unwrapCollection(payload?.data?.list || payload?.data || payload)
    .map((item: any) => ({
      id: Number(item.id),
      firstname: item.firstname || '',
      lastname: item.lastname || '',
    }))
    .filter((item: ManagerOption) => item.id && (!excludeId || item.id !== Number(excludeId)));

export const isFilled = (value?: string | number | null) => String(value ?? '').trim() !== '';

export const isFilledPhone = (value?: string | null) => {
  const phone = (value || '').trim();
  if (!phone) {
    return false;
  }
  try {
    return isValidPhoneNumber(phone);
  } catch {
    return false;
  }
};

export const hasRequiredBeneficiaryInfos = (infos: {
  firstname: string;
  lastname: string;
  postal_code: string;
  town: string;
  phone: string;
  language: number | '';
}) =>
  isFilled(infos.firstname) &&
  isFilled(infos.lastname) &&
  isFilled(infos.postal_code) &&
  isFilled(infos.town) &&
  isFilledPhone(infos.phone) &&
  infos.language !== '';
